// app/features/blog/components/BlogTableOfContentsComponent.tsx
'use client';

import { useEffect, useState } from 'react';

import type { BlogArticleContent } from '../blog.interface';

// Some headings come in from the DB still carrying their markdown prefix (e.g. "## TL;DR").
// We strip any leading `#` characters and trim whitespace before displaying or slugifying.
function cleanHeading(raw: string): string {
  return raw.replace(/^#+\s*/, '').trim();
}

function slugify(text: string): string {
  return cleanHeading(text)
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

interface BlogTableOfContentsComponentProps {
  content: BlogArticleContent | null;
}

export default function BlogTableOfContentsComponent({
  content,
}: BlogTableOfContentsComponentProps) {
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    if (!content?.sections?.length) return;

    const ids = content.sections.map((s) => slugify(s.heading));
    const elements = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => !!el);

    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort(
            (a, b) =>
              (a.target as HTMLElement).offsetTop -
              (b.target as HTMLElement).offsetTop,
          );
        if (visible[0]) {
          setActiveId(visible[0].target.id);
        }
      },
      { rootMargin: '-100px 0px -60% 0px', threshold: 0 },
    );

    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [content]);

  if (!content || !content.sections || content.sections.length === 0) {
    return null;
  }

  return (
    <nav aria-label="Table of contents">
      <p className="text-[11px] font-semibold text-text-tertiary uppercase tracking-[0.18em] mb-4">
        On this page
      </p>
      <ul className="space-y-3">
        {content.sections.map((section, index) => {
          const clean = cleanHeading(section.heading);
          const id = slugify(section.heading);
          const isActive = activeId === id;
          return (
            <li key={index}>
              <a
                href={`#${id}`}
                className={
                  'block text-[13px] leading-snug transition-colors ' +
                  (isActive
                    ? 'text-teal-dark font-semibold'
                    : 'text-text-tertiary hover:text-gray-900')
                }
              >
                {clean}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
